import React, { Component } from "react";
import { DialogActions, Typography, Icon } from "@material-ui/core";
import styled from "styled-components";
import {
    BeautifulDialog,
    ShowSnackBar
} from "js/react/components/Dialogs/BaseDialog";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { getStaticUrl } from "js/config";
import { BlueButton } from "js/react/components/UiComponents";
import getLogger from "js/core/logger";
import { app } from "js/namespaces";
import * as analytics from "js/analytics";
import { trackActivity } from "js/core/utilities/utilities";

const logger = getLogger();

const DialogContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ImageContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
`;

const StyledImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
`;

const Title = styled.div`
    color: #222222;
    font-size: 23px;
    font-family: Source Sans Pro;
    font-weight: 600;
    letter-spacing: 0.50px;
    word-wrap: break-word;
`;

const Description = styled.div`
    color: #222222;
    font-size: 16px;
    font-family: Source Sans Pro;
    font-weight: 400;
    line-height: 20px;
    word-wrap: break-word;
`;

const CopyAndEmailInstructions = styled.div`
    color: #666666;
    font-size: 16px;
    font-family: Source Sans Pro;
    font-style: italic;
    font-weight: 400;
    line-height: 20px;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
    padding: 24px;
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

export default class EditOnDesktopDialog extends Component {
    constructor(props) {
        super(props);
        // Initialize state as null - we'll determine orientation after mount
        // This prevents showing incorrect layout on initial render
        this.state = {
            isMobileLandscape: null
        };
    }

    // Helper method to detect landscape orientation
    // Uses matchMedia API for more reliable detection, falls back to window dimensions
    getIsLandscape = () => {
        if (typeof window !== "undefined" && window.matchMedia) {
            try {
                const landscapeQuery = window.matchMedia("(orientation: landscape)");
                if (landscapeQuery && landscapeQuery.matches !== undefined) {
                    return landscapeQuery.matches;
                }
            } catch (e) {
                // Fall through to dimension-based check
            }
        }
        return window.innerWidth > window.innerHeight;
    };

    componentDidMount() {
        // Determine orientation after mount to ensure accurate detection
        this.handleResize();

        // Listen for window resize events (which includes orientation changes)
        window.addEventListener("resize", this.handleResize);
        // Also listen for orientationchange event for better mobile support
        window.addEventListener("orientationchange", this.handleOrientationChange);
    }

    componentWillUnmount() {
        // Clean up event listeners
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("orientationchange", this.handleOrientationChange);
    }

    handleResize = () => {
        // Update state when window is resized (including orientation changes)
        // Add a small delay to ensure window dimensions are updated
        setTimeout(() => {
            this.setState({
                isMobileLandscape: this.getIsLandscape()
            });
        }, 50);
    };

    handleOrientationChange = () => {
        // Handle orientation change specifically
        // Add a delay to ensure window dimensions are updated after orientation change
        setTimeout(() => {
            this.setState({
                isMobileLandscape: this.getIsLandscape()
            });
        }, 100);
    };

    copyLink = async () => {
        const { presentation } = this.props;

        trackActivity("Mobile", "CopyShareLink");

        // Get the editor link for the presentation
        let url;
        if (presentation && presentation.id) {
            url = `${window.location.origin}/${presentation.id}`;
        } else {
            // Fall back to current window location
            url = window.location.href;
        }

        try {
            // Try to use native share dialog on mobile/tablet, fall back to clipboard
            if (app.isMobileOrTablet && window.navigator.share) {
                await window.navigator.share({ url });
            } else {
                await clipboardWrite({ [ClipboardType.TEXT]: url });
                ShowSnackBar({
                    message: "Link copied to clipboard"
                });
            }
        } catch (err) {
            // If share was cancelled, don't show an error
            if (err.name === "AbortError") {
                return;
            }
            logger.error(err, "[EditOnDesktopDialog] Failed to copy/share link");
            ShowSnackBar({
                message: "Failed to copy link"
            });
        }
    };

    render() {
        const { closeDialog } = this.props;
        const { isMobileLandscape } = this.state;

        // Don't render until we've determined the orientation
        if (isMobileLandscape === null) {
            return null;
        }

        return (
            <BeautifulDialog
                closeDialog={closeDialog}
                PaperProps={isMobileLandscape ? { style: { maxWidth: 350 } } : undefined}
            >
                <DialogContainer>
                    {!isMobileLandscape && (<ImageContainer>
                        <StyledImage
                            src={getStaticUrl("/images/mobile/editOnDesktop.svg")}
                            alt="Edit on desktop"
                        />
                    </ImageContainer>)}
                    <ContentContainer>
                        <TextContainer>
                            <Title>Edit on desktop</Title>
                            <Description>
                                You need to open this on desktop to access the full set of editing and theming tools.
                            </Description>
                            <CopyAndEmailInstructions>
                                Copy and email this link to yourself, then open on desktop.
                            </CopyAndEmailInstructions>
                        </TextContainer>
                        <ButtonContainer>
                            <BlueButton
                                fullWidth={true}
                                size="large"
                                color="primary"
                                variant="contained"
                                onClick={this.copyLink}
                                startIcon={<Icon>share</Icon>}
                            >
                                COPY LINK
                            </BlueButton>
                        </ButtonContainer>
                    </ContentContainer>
                </DialogContainer>
            </BeautifulDialog>
        );
    }
}

